import { Ranking, RowType, Variant } from '@opositandopn/ui';
import classnames from 'classnames';

import { BaseComponentProps } from '../../../../types/base-component.types';
import { formatFromSeconds } from '../../../../utils/date';
import { formatGrade } from '../../../../utils/numbers';
import { AssessmentMetricsViewModel } from '../../api/useMultipleChoiceAssessments';
import { AssessmentMetrics } from './layout/AssessmentMetrics';

import styles from './AssessmentScoreboard.module.scss';

/** How many of the scores the API returns make it onto the board. */
const TOP_POSITIONS = 5;

/** The API leaves a score anonymous either by omitting the name or by sending this. */
const ANONYMOUS_USERNAME = 'unknown';

/** Stands in for a figure the metrics did not carry. */
const UNKNOWN_FIGURE = '—';

/**
 * The size of the field, written under the board. Only ever read alongside a
 * position, so it is spelt out in the same words the position is read in.
 */
function formatParticipants(total: number): string {
  return `${total.toLocaleString('es')} ${
    total === 1 ? 'participante' : 'participantes'
  }`;
}

/**
 * Whether there is a field to read at all: more than one mark on the board. A board
 * of one puts the reader first out of one, which says nothing their own mark has not
 * already said, so the places it hangs behind an icon leave the icon off.
 */
function hasContestedField(metrics: AssessmentMetricsViewModel): boolean {
  return metrics.total > 1;
}

/** How far down the field a mark under the cut-off stops being amber and turns red. */
const DANGER_SHARE = 0.75;

/**
 * How the reader's row reads. Their own mark decides it: at or above the cut-off is
 * a pass, whatever the field did that month. Below it, red is kept for the foot of
 * the board — a mark under the cut-off is the ordinary case months out from the
 * exam, and a board that shouts at everyone under it says nothing.
 *
 * How far down the field they sit comes from `positionShare` rather than from the
 * position and the total on the board: those two are not on one scale, since the API
 * reports the leading positions as they stand and inflates the rest.
 */
function variantFor(
  grade: number | undefined,
  cutoffScore: number,
  positionShare: number | undefined,
): Variant | undefined {
  if (grade === undefined) {
    return undefined;
  }

  if (grade >= cutoffScore) {
    return 'success';
  }

  return positionShare !== undefined && positionShare > DANGER_SHARE
    ? 'danger'
    : 'warning';
}

function formatUsername(username: string | undefined): string {
  return username === undefined ||
    username === '' ||
    username === ANONYMOUS_USERNAME
    ? 'Desconocido'
    : username;
}

type ContainerProps = BaseComponentProps & {
  metrics: AssessmentMetricsViewModel;
  /**
   * What to call the reader on their own row: past the top scores the metrics send
   * no name, only the figures. Passed in rather than read from the profile here, so
   * the board stays renderable without that context.
   */
  ownUsername?: string;
  /** The assessment's own declared cut-off; see {@link AssessmentMetrics}. */
  cutoffScore?: number;
};

function AssessmentScoreboard({
  className,
  metrics,
  ownUsername,
  cutoffScore,
  testId = 'ui-assessment-scoreboard',
}: ContainerProps) {
  // Only a reader who has sat the assessment is given a position; -1 then marks a
  // board with no reader on it, which no row can match.
  //
  // The position doubles as an index into the top scores, to know which of them to
  // mark. That holds because the API inflates no position below the sixteenth while
  // it marks only the first five: a position that reaches the mapping is its own
  // index, and an inflated one is far past the end.
  //
  // Only the marking is read off the top scores. The figures come from `ownScore`,
  // which the API sends whatever the position, taken from the same ranked attempt
  // the position was counted from — so the row can never quote a sitting the ranking
  // did not count.
  const ownIndex = metrics.position ? metrics.position - 1 : -1;

  const ownVariant = variantFor(
    metrics.ownScore?.grade,
    cutoffScore ?? metrics.cutoffScore,
    metrics.positionShare,
  );

  // Every row reads the same three figures, as the API hands them over: the times on
  // the board are already read at the assessment's own limit, so an attempt left open
  // and handed in hours later does not write an afternoon into a row here. Only the
  // reader's own can be missing a figure — a client running ahead of the API sends no
  // `ownScore` — and one the board cannot fill is left blank rather than guessed.
  const cellsFor = (
    username: string | undefined,
    grade: number | undefined,
    elapsedTime: number | undefined,
  ) => [
    formatUsername(username),
    grade !== undefined ? formatGrade(grade) : UNKNOWN_FIGURE,
    elapsedTime !== undefined ? formatFromSeconds(elapsedTime) : UNKNOWN_FIGURE,
  ];

  const rows: RowType[] = metrics.scores
    .slice(0, TOP_POSITIONS)
    .map((userScore, index) => ({
      cells: cellsFor(
        userScore.username,
        userScore.grade,
        userScore.elapsedTime,
      ),
      highlighted: index === ownIndex,
      variant: index === ownIndex ? ownVariant : undefined,
    }));

  // Placing outside the board still earns a row, appended under the top few and
  // carrying the real position: where the reader stands is the figure they came for,
  // and the board is the only place it is read.
  if (ownIndex >= TOP_POSITIONS) {
    rows.push({
      position: ownIndex + 1,
      highlighted: true,
      variant: ownVariant,
      cells: cellsFor(
        ownUsername,
        metrics.ownScore?.grade,
        metrics.ownScore?.elapsedTime,
      ),
    });
  }

  // The board always stands its five places deep, the unclaimed ones written as
  // blanks: a simulacro's card keeps one shape from the week it is published to the
  // week it is full, rather than growing a row with every finisher.
  while (rows.length < TOP_POSITIONS) {
    rows.push({
      cells: [UNKNOWN_FIGURE, UNKNOWN_FIGURE, UNKNOWN_FIGURE],
    });
  }

  return (
    <div
      className={classnames(styles['scoreboard'], className)}
      data-testid={testId}
    >
      <AssessmentMetrics
        className={styles['scoreboard__header']}
        metrics={metrics}
        cutoffScore={cutoffScore}
      />
      <Ranking
        className={styles['scoreboard__body']}
        columns={['Usuario', 'Nota', 'Tiempo']}
        rows={rows}
        // Written for a reader who has sat the assessment, and for nobody else: the
        // size of the field is how far their own position reaches, and on a board
        // they are not on it is a number with nothing to read it against.
        footer={
          metrics.position ? formatParticipants(metrics.total) : undefined
        }
      />
    </div>
  );
}

export { AssessmentScoreboard, hasContestedField };
